/**
 * Running one calibration, which is a conversation rather than a call.
 *
 * The shape that drives this component: `startCalibration` returns the moment
 * the aircraft accepts the command, and everything that matters happens after.
 * ArduPilot's six-position accelerometer routine asks for a position, waits,
 * and only moves on when told the airframe is there. So the panel polls, and
 * the state it has to render best is `waiting_for_position` — a spinner is
 * exactly wrong there, because the aircraft is waiting on the operator, not the
 * operator on the aircraft.
 *
 * Two rules about what is shown:
 *
 * Every line the autopilot said is shown verbatim, newest first. The server
 * classifies only terminal states and never rewrites a line, and neither does
 * this: the reference implementation this replaces matched on words like
 * "level", so "Battery level low" moved its state machine into an
 * accelerometer calibration.
 *
 * The acknowledgement echoes the position the aircraft asked for, never a
 * counter this component keeps. ArduPilot does not necessarily walk the six
 * positions in order, and confirming a step it is not on is silently ignored —
 * which reads to an operator as the aircraft having hung.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import {
  acknowledgePosition,
  cancelCalibration,
  getCalibrationKinds,
  getCalibrationStatus,
  startCalibration,
  type CalibrationKindInfo,
  type CalibrationKindName,
  type CalibrationStatus,
} from '@/api/diagnostics';
import { useCanControl } from '@/stores/authStore';
import type { DroneId } from '@/types/domain';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

/** Fast enough that a position prompt does not feel late; slow enough that it
 * is not a second telemetry stream. The prompt arrives within milliseconds of
 * the command, so the first poll is fired immediately rather than waited for. */
const POLL_MS = 700;

const STATE_COLOR: Record<string, string> = {
  running: WARN,
  waiting_for_position: WARN,
  succeeded: OK,
  failed: DANGER,
};

const STATE_LABEL: Record<string, string> = {
  running: 'RUNNING',
  waiting_for_position: 'WAITING FOR YOU',
  succeeded: 'SUCCEEDED',
  failed: 'FAILED',
};

export function CalibrationRunner({ droneId }: { droneId: DroneId }): ReactNode {
  const canCommand = useCanControl(droneId);
  const [kinds, setKinds] = useState<CalibrationKindInfo[]>([]);
  const [selected, setSelected] = useState<CalibrationKindName | null>(null);
  const [status, setStatus] = useState<CalibrationStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Which position we have already confirmed, so a poll landing before the
  // aircraft moves on does not re-arm the button under the operator's finger.
  const acknowledged = useRef<number | null>(null);

  useEffect(() => {
    let live = true;
    void getCalibrationKinds(droneId).then((r) => {
      if (live) setKinds(r.ok ? r.data.kinds : []);
    });
    return () => {
      live = false;
    };
  }, [droneId]);

  const poll = useCallback(() => {
    void getCalibrationStatus(droneId).then((r) => {
      if (r.ok) setStatus(r.data);
    });
  }, [droneId]);

  useEffect(() => {
    setStatus(null);
    setError(null);
    acknowledged.current = null;
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => window.clearInterval(id);
  }, [droneId, poll]);

  const spec = kinds.find((k) => k.kind === selected) ?? null;
  const active = status?.active === true;
  const state = status?.state;
  const runningSpec = kinds.find((k) => k.kind === status?.kind) ?? null;
  const waiting = state === 'waiting_for_position' && typeof status?.position === 'number';
  const alreadyConfirmed = waiting && acknowledged.current === status?.position;

  async function run() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    acknowledged.current = null;
    const result = await startCalibration(droneId, selected);
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    if (!result.data.result.ok) {
      // The aircraft refused, or answered UNSUPPORTED. Its own words, not ours:
      // ArduPilot returns UNSUPPORTED for some of these even when they worked.
      setError(result.data.result.message);
    }
    poll();
  }

  async function confirmPosition() {
    const position = status?.position;
    if (typeof position !== 'number') return;
    setBusy(true);
    const result = await acknowledgePosition(droneId, position);
    setBusy(false);
    if (result.ok) acknowledged.current = position;
    else setError(result.error.message);
    poll();
  }

  async function stop() {
    setBusy(true);
    const result = await cancelCalibration(droneId);
    setBusy(false);
    if (!result.ok) setError(result.error.message);
    poll();
  }

  return (
    <div className="flex flex-col gap-3">
      {!canCommand && (
        <p className="text-[11px]" style={{ color: MUTED }}>
          Read-only: starting a calibration needs operator role and control of this aircraft.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {kinds.map((kind) => {
          const isSelected = selected === kind.kind;
          return (
            <button
              key={kind.kind}
              type="button"
              disabled={active || !canCommand}
              onClick={() => setSelected(kind.kind)}
              className="rounded-[99px] border px-3 py-[5px] label-mono text-[10px] transition-colors disabled:opacity-40"
              style={{
                borderColor: isSelected ? OK : 'rgba(255,255,255,0.16)',
                background: isSelected ? 'color-mix(in oklch, oklch(0.78 0.15 155) 16%, transparent)' : 'transparent',
                color: isSelected ? OK : 'rgba(244,244,245,0.75)',
              }}
            >
              {kind.label.toUpperCase()}
            </button>
          );
        })}
      </div>

      {spec && !active && (
        <div
          className="rounded-[12px] border px-3 py-2.5 text-[11px]"
          style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.13)' }}
        >
          <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
            BEFORE YOU START
          </span>
          <p className="mt-1" style={{ color: 'rgba(244,244,245,0.85)' }}>
            {spec.precondition || 'No physical preparation needed.'}
          </p>
          {/* No second sentence about the six positions here. The server's
              precondition already says it, and printing our own next to it put
              two near-identical warnings on screen — which is how an operator
              learns to skim the box that exists to be read. */}
        </div>
      )}

      {!active && (
        <button
          type="button"
          disabled={!selected || busy || !canCommand}
          onClick={() => void run()}
          className="w-fit rounded-[10px] border px-4 py-2 label-mono text-[11px] disabled:opacity-40"
          style={{
            borderColor: 'color-mix(in oklch, oklch(0.78 0.15 155) 45%, transparent)',
            background: 'color-mix(in oklch, oklch(0.78 0.15 155) 14%, transparent)',
            color: OK,
          }}
        >
          {busy ? 'SENDING…' : 'START CALIBRATION'}
        </button>
      )}

      {active && (
        <div
          className="rounded-[14px] border px-3.5 py-3"
          style={{
            background: 'rgba(255,255,255,0.04)',
            borderColor: `color-mix(in oklch, ${STATE_COLOR[state ?? 'running'] ?? WARN} 40%, transparent)`,
          }}
        >
          <div className="flex items-center gap-2">
            <span className="label-mono text-[11px]">{runningSpec?.label.toUpperCase() ?? status?.kind?.toUpperCase()}</span>
            <span
              className="rounded-[99px] px-2 py-0.5 label-mono text-[9px]"
              style={{
                color: STATE_COLOR[state ?? 'running'] ?? WARN,
                background: `color-mix(in oklch, ${STATE_COLOR[state ?? 'running'] ?? WARN} 16%, transparent)`,
              }}
            >
              {STATE_LABEL[state ?? 'running'] ?? 'RUNNING'}
            </span>
            {/* Always offered, including for a kind the aircraft will not
                cancel. `cancellable` describes whether the AIRCRAFT honours a
                cancel, not whether the operator may stop watching: the server
                drops the session either way, precisely so a routine nobody is
                coming back to cannot leave this panel reporting RUNNING for
                ever. Without this the six-position calibration was a trap — an
                airframe that never prompts left no way out of the screen. */}
            {canCommand && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void stop()}
                className="ml-auto rounded-[8px] border px-2.5 py-1 label-mono text-[9.5px] disabled:opacity-40"
                style={{ borderColor: 'color-mix(in oklch, oklch(0.72 0.19 28) 45%, transparent)', color: DANGER }}
                title={
                  runningSpec?.cancellable === false
                    ? 'The aircraft may not stop this routine. This clears it from the panel and says what the aircraft answered.'
                    : 'Ask the aircraft to stop this calibration.'
                }
              >
                {runningSpec?.cancellable === false ? 'ABANDON' : 'CANCEL'}
              </button>
            )}
          </div>

          {status?.instruction && (
            <p className="mt-2 text-[12px]" style={{ color: '#f4f4f5' }}>
              {status.instruction}
            </p>
          )}

          {waiting && (
            <button
              type="button"
              disabled={busy || alreadyConfirmed || !canCommand}
              onClick={() => void confirmPosition()}
              className="mt-2.5 rounded-[10px] border px-4 py-2 label-mono text-[11px] disabled:opacity-40"
              style={{
                borderColor: 'color-mix(in oklch, oklch(0.80 0.15 85) 50%, transparent)',
                background: 'color-mix(in oklch, oklch(0.80 0.15 85) 16%, transparent)',
                color: WARN,
              }}
            >
              {alreadyConfirmed ? 'CONFIRMED — WAITING' : `IN POSITION ${status?.position}`}
            </button>
          )}

          {status?.compassPercent && Object.keys(status.compassPercent).length > 0 && (
            <div className="mt-2.5 flex flex-col gap-1.5">
              {Object.entries(status.compassPercent).map(([compass, percent]) => (
                <div key={compass} className="flex items-center gap-2 text-[11px]">
                  <span className="label-mono w-[74px] shrink-0" style={{ color: MUTED }}>
                    {compass.toUpperCase()}
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-[3px]" style={{ background: 'rgba(0,0,0,0.35)' }}>
                    <span className="block h-full" style={{ width: `${percent}%`, background: OK }} />
                  </div>
                  <span className="tnum w-9 shrink-0 text-right" style={{ color: MUTED }}>
                    {percent}%
                  </span>
                </div>
              ))}
            </div>
          )}

          {status?.lines && status.lines.length > 0 && (
            <div
              className="scroll-thin mt-2.5 max-h-[132px] overflow-y-auto rounded-[10px] px-2.5 py-2"
              style={{ background: 'rgba(0,0,0,0.3)' }}
            >
              {status.lines.map((line, i) => (
                <p key={`${i}-${line}`} className="text-[10.5px] leading-[1.55]" style={{ color: 'rgba(244,244,245,0.7)' }}>
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-[11px]" style={{ color: DANGER }}>
          {error}
        </p>
      )}
    </div>
  );
}
